import React, { useState, useMemo, useCallback } from 'react';
import { isInitialQueryLoading } from '../../../lib/query/listQuery';
import { DatePicker, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import DataTable from '../../../components/DataTable';
import { ListPage, FilterBar } from '../../../components/list';
import { useContactOptions } from '../../../hooks/options/useContactOptions';
import { useLeadOptions } from '../../../hooks/options/useLeadOptions';
import { useCommissions } from '../hooks/useCommissions';
import { commissionReportColumns } from '../commissionColumns';
import { COMMISSIONS_PAID_LABEL } from '../copy';
import type { CommissionItem } from '../types';

dayjs.extend(utc);

const { Text } = Typography;
const { RangePicker } = DatePicker;

const CommissionReportsPage: React.FC = () => {
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    () => [dayjs().startOf('month'), dayjs()],
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [leadSearch, setLeadSearch] = useState('');

  const { data: agents = [] } = useContactOptions({ role: 0 });
  const { data: allContacts = [] } = useContactOptions();
  const { data: leads = [] } = useLeadOptions({ search: leadSearch || undefined });

  const agentOptions = useMemo(
    () =>
      agents.map((c) => ({
        value: c.id,
        label: c.label,
      })),
    [agents],
  );

  const contactOptions = useMemo(
    () =>
      allContacts.map((c) => ({
        value: c.id,
        label: c.label,
      })),
    [allContacts],
  );

  const leadOptions = useMemo(
    () =>
      leads.map((l) => ({
        value: l.id,
        label: l.label,
      })),
    [leads],
  );

  const queryParams = useMemo(() => {
    if (!dateRange) {
      return null;
    }

    const [from, to] = dateRange;
    return {
      agentIds: selectedAgentIds,
      leadIds: selectedLeadIds,
      contactIds: selectedContactIds,
      dateFrom: from.utc(true).startOf('day').toISOString(),
      dateTo: to.utc(true).endOf('day').toISOString(),
      pageNumber: page,
      pageSize,
    };
  }, [
    selectedAgentIds,
    selectedLeadIds,
    selectedContactIds,
    dateRange,
    page,
    pageSize,
  ]);

  const { data: paged, isLoading: commissionsLoading } = useCommissions(queryParams);
  const isLoading = isInitialQueryLoading(commissionsLoading, paged);

  const totalCommissionSum = useMemo(
    () =>
      (paged?.items ?? []).reduce(
        (sum, item) => sum + item.commissionAmount,
        0,
      ),
    [paged?.items],
  );

  const handleResetFilters = useCallback(() => {
    setSelectedAgentIds([]);
    setSelectedLeadIds([]);
    setSelectedContactIds([]);
    setDateRange(null);
    setLeadSearch('');
    setPage(1);
  }, []);

  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  }, []);

  return (
    <ListPage
      title={COMMISSIONS_PAID_LABEL}
      subtitle="Saved payout ledger — filter by payout date and optionally by agent, lead, or contact."
      filters={(
        <FilterBar
          onReset={
            selectedAgentIds.length > 0 ||
            selectedLeadIds.length > 0 ||
            selectedContactIds.length > 0 ||
            dateRange
              ? handleResetFilters
              : undefined
          }
        >
          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
              setPage(1);
            }}
          />
          <Select<string[]>
            mode="multiple"
            allowClear
            placeholder="Agent(s)"
            showSearch
            optionFilterProp="label"
            options={agentOptions}
            value={selectedAgentIds}
            onChange={(v) => {
              setSelectedAgentIds(v);
              setPage(1);
            }}
            style={{ minWidth: 220 }}
          />
          <Select<string[]>
            mode="multiple"
            allowClear
            placeholder="Lead(s)"
            showSearch
            filterOption={false}
            onSearch={setLeadSearch}
            optionFilterProp="label"
            options={leadOptions}
            value={selectedLeadIds}
            onChange={(v) => {
              setSelectedLeadIds(v);
              setPage(1);
            }}
            style={{ minWidth: 220 }}
          />
          <Select<string[]>
            mode="multiple"
            allowClear
            placeholder="Contact(s)"
            showSearch
            optionFilterProp="label"
            options={contactOptions}
            value={selectedContactIds}
            onChange={(v) => {
              setSelectedContactIds(v);
              setPage(1);
            }}
            style={{ minWidth: 220 }}
          />
        </FilterBar>
      )}
    >
      <DataTable<CommissionItem>
        columns={commissionReportColumns}
        dataSource={paged?.items ?? []}
        loading={isLoading}
        rowKey="id"
        bordered
        striped
        pagination={
          paged
            ? {
                current: paged.pageNumber,
                pageSize: paged.pageSize,
                total: paged.totalCount,
                onChange: handlePaginationChange,
              }
            : undefined
        }
        emptyText={
          dateRange
            ? 'No payout records found'
            : 'Select a payout date range to view records'
        }
      />

      {paged && paged.items.length > 0 && (
        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <Text strong>
            Total paid (this page):{' '}
            {totalCommissionSum.toLocaleString('en-US', {
              minimumFractionDigits: 2,
            })}
          </Text>
        </div>
      )}
    </ListPage>
  );
};

export default CommissionReportsPage;
